import { useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Typography,
  useMediaQuery
} from "@material-ui/core";
import { alpha, useTheme } from "@material-ui/core/styles";
import { Close, Layers } from "@material-ui/icons";
import { useTranslation } from "react-i18next";
import { LocationGroupsManager } from "./LocationGroupsManager";
import type { LocationGroup } from "../../../@types/sharedSchema";

/**
 * The manager, as a modal — what the picker's cog opens.
 *
 * Structure, spacing and button vocabulary follow `EditContactDialog`: a header
 * row carrying a badge, a title and a subtitle with the close ✕ at the end, a
 * rule under it so the fixed header has a real edge while the body scrolls, and
 * a bordered `DialogActions` strip.
 *
 * ⚠️ The host must clear the picker's `Popper` anchor BEFORE opening this — the
 * popper sits at `zIndex: 1300`, which is MUI's own default modal level, so a
 * popper left open renders through the dialog.
 *
 * ⚠️ IT MUST SWALLOW ITS OWN KEYSTROKES. React bubbles synthetic events out of a
 * portal along the COMPONENT tree, not the DOM tree, so every key pressed in here
 * still reaches whatever rendered the picker. One of those hosts is the CRM cell
 * editor, which listens for Enter on a container above the control and reads it as
 * "commit and close" (`CrmCellEditor.tsx` → `readEditorKey`). Without the guard,
 * pressing Enter after typing a group name — the natural submit reflex — saves the
 * cell, unmounts the editor, and takes this dialog and the half-written group with
 * it, silently. MUI chains a caller's `onKeyDown` BEFORE its own Escape handling
 * (`ModalUnstyled.js`), so stopping propagation here costs nothing: Escape still
 * closes the dialog.
 */

type LocationGroupsManagerDialogProps = {
  open: boolean;
  onClose: () => void;
  /**
   * When set, the rows are pickable. Picking CLOSES the dialog: the result of a
   * pick is a chip on the picker behind it, and a modal that stays put after a
   * successful action reads as one that did nothing.
   */
  onPickGroup?: (group: LocationGroup) => void;
  /**
   * Open straight into this group's editor. Set when the dialog was opened by a
   * click on a group CHIP: the realtor has already pointed at the group they mean,
   * so a list to pick it out of again is a step backwards.
   */
  initialEditGroupId?: string;
};

export function LocationGroupsManagerDialog({
  open,
  onClose,
  onPickGroup,
  initialEditGroupId
}: LocationGroupsManagerDialogProps): JSX.Element {
  const theme = useTheme();
  const { t } = useTranslation();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  /**
   * Mirrored up from the manager, which swaps its body for the editor in place.
   * The footer below is a CONTAINED PRIMARY button, and so is the editor's own
   * "שמירה" — two of them side by side, one of which discards the draft, is a
   * choice nobody should be asked to make. On mobile the dialog is full-screen,
   * so the footer is the bar pinned to the bottom of the phone while "שמירה" is
   * somewhere up the scroll: the wrong one is the one under your thumb.
   */
  const [editing, setEditing] = useState(false);
  /**
   * The open editor's own dirty flag, and the guard it feeds.
   *
   * Same shape as the contact dialog's: EVERY user-initiated close — backdrop,
   * Escape, the ✕, the footer button — goes through `requestClose`, and a
   * programmatic close (there is none here yet) would call `onClose` directly.
   * Without it, a click anywhere outside the modal silently discards a group the
   * realtor may have spent a minute picking fifty areas into.
   */
  const [dirty, setDirty] = useState(false);
  const [confirmDiscardOpen, setConfirmDiscardOpen] = useState(false);

  const requestClose = (): void => {
    if (dirty) {
      setConfirmDiscardOpen(true);
      return;
    }
    onClose();
  };

  const discardAndClose = (): void => {
    setConfirmDiscardOpen(false);
    setDirty(false);
    onClose();
  };

  return (
    <>
      <Dialog
        fullWidth
        maxWidth="sm"
        fullScreen={isMobile}
        open={open}
        onClose={requestClose}
        onKeyDown={(event) => event.stopPropagation()}
        PaperProps={{ sx: { borderRadius: isMobile ? 0 : "22px" } }}
      >
        <DialogTitle sx={{ px: 3, pt: 2.25, pb: 0 }}>
          <Stack direction="row" alignItems="center" spacing={1.75}>
            <Box
              sx={{
                width: 48,
                height: 48,
                flex: "none",
                borderRadius: "16px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "warning.dark",
                backgroundColor: alpha(theme.palette.warning.main, 0.16)
              }}
            >
              <Layers />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              {/* `component="div"`: `DialogTitle` renders an <h2>, and a <p> — which
                  is what a default Typography emits — is not valid inside one. */}
              <Typography component="div" noWrap sx={{ fontSize: 19, fontWeight: 800, lineHeight: 1.2 }}>
                {t("locationGroups.title")}
              </Typography>
              <Typography
                component="div"
                variant="body2"
                sx={{ mt: 0.25, color: "text.secondary", whiteSpace: "normal" }}
              >
                {t("locationGroups.subtitle")}
              </Typography>
            </Box>
            <Box sx={{ flex: 1 }} />
            <IconButton
              size="small"
              onClick={requestClose}
              sx={{ alignSelf: "flex-start", bgcolor: alpha(theme.palette.grey[500], 0.12) }}
            >
              <Close fontSize="small" />
            </IconButton>
          </Stack>
        </DialogTitle>

        <Divider sx={{ mt: 2.25 }} />

        <DialogContent sx={{ pt: 2.5, pb: 3, px: 3 }}>
          <LocationGroupsManager
            dense
            // Remounts the manager when the dialog is reopened for a DIFFERENT group,
            // so the auto-open runs again instead of being suppressed as already
            // honoured — and so a half-typed edit never survives into another group.
            key={initialEditGroupId ?? "list"}
            initialEditGroupId={initialEditGroupId}
            onEditingChange={setEditing}
            onDirtyChange={setDirty}
            onPickGroup={
              onPickGroup
                ? (group) => {
                    onPickGroup(group);
                    onClose();
                  }
                : undefined
            }
          />
        </DialogContent>

        {!editing && (
          <DialogActions sx={{ px: 3, py: 1.75, borderTop: "1px solid", borderColor: "divider" }}>
            <Box sx={{ flex: 1 }} />
            <Button
              variant="contained"
              onClick={requestClose}
              sx={{ fontWeight: 800, borderRadius: "12px", px: 3 }}
            >
              {t("locationGroups.done")}
            </Button>
          </DialogActions>
        )}
      </Dialog>

      {/* Discard guard — the same shape and wording the contact dialog uses, so a
          realtor meets one "you have unsaved changes" in the product, not two.
          A sibling of the manager dialog rather than a child of it: nesting would
          put it under the outer dialog's own `onKeyDown` swallow, and Escape would
          stop reaching it. */}
      <Dialog
        open={confirmDiscardOpen}
        maxWidth="xs"
        onClose={() => setConfirmDiscardOpen(false)}
        PaperProps={{ sx: { borderRadius: "16px" } }}
      >
        <DialogContent sx={{ pt: 3 }}>
          <Typography sx={{ fontSize: 16, fontWeight: 800, mb: 0.5 }}>
            {t("locationGroups.discard.title")}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t("locationGroups.discard.body")}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            color="inherit"
            onClick={discardAndClose}
            sx={{ color: "text.secondary", fontWeight: 700 }}
          >
            {t("locationGroups.discard.confirm")}
          </Button>
          <Button
            variant="contained"
            onClick={() => setConfirmDiscardOpen(false)}
            sx={{ fontWeight: 700 }}
          >
            {t("locationGroups.discard.cancel")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
